import { useState, type ReactNode } from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Toolbar from '@mui/material/Toolbar';
import Tooltip from '@mui/material/Tooltip';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import HelpOutlinedIcon from '@mui/icons-material/HelpOutlined';
import LightModeIcon from '@mui/icons-material/LightMode';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import MenuIcon from '@mui/icons-material/Menu';
import { useColorScheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useViewLevel } from '@eis/core';
import { JobsTray } from '@eis/patterns';
import { persistLanguage } from '../i18n';
import { ProfileChip } from './ProfileChip';
import { useChromeVariant } from './useChromeVariant';

/** Chrome that must keep its intrinsic width — everything in the bar. */
const FIXED = { flexShrink: 0 } as const;

/** Screen-reader-only text, the MUI `visuallyHidden` recipe. */
const SCREEN_READER_ONLY = {
  border: 0,
  clip: 'rect(0 0 0 0)',
  height: '1px',
  margin: '-1px',
  overflow: 'hidden',
  padding: 0,
  position: 'absolute',
  whiteSpace: 'nowrap',
  width: '1px',
} as const;

export interface AppTopBarProps {
  /** Omitted on the no-access screen, which has no drawer to toggle. */
  readonly onToggleNav?: () => void;
  readonly navCollapsed?: boolean;
  /**
   * DOM id of the drawer the toggle controls, for `aria-controls`.
   *
   * Passed in rather than hard-coded because the top bar does not own that element: rendered on
   * its own — in a story, or on a screen with no drawer — a hard-coded id would be a dangling
   * reference, which is an actual axe failure and not a theoretical one.
   */
  readonly navId?: string;
}

/**
 * Top bar: drawer toggle, ViewLevel toggle, help, language switch, colour scheme, the jobs tray
 * (SDD-020 §9) and the profile chip. Berry (EIS Design System, prompt 24 Phase 24.2): white, a hairline under it, 56 px, DE/EN as
 * a segmented toggle. The global search placeholder is gone — it searched nothing, and every list
 * page has its own search (owner's decision, 2026-10-01); a mail button waits for notifications.
 *
 * ViewLevel and language sit here rather than inside a settings page because both change what is
 * on screen right now — burying them costs more than the strip of chrome they occupy.
 *
 * In the suite variant (SDD-012, U-5' and the gate's Q-2) the bar is the legacy header's
 * reduced twin: help and profile as the legacy shows them, ViewLevel and language kept because
 * they are functions and not chrome, and the search box, the colour-scheme toggle and the
 * tasks/notifications slots hidden — search does not exist yet, the legacy has no dark mode,
 * and tasks and notifications stay in the legacy header until their widgets are embedded here
 * (icon-map.json `shownInSuiteVariant`).
 *
 * # Nothing in the bar shrinks
 *
 * A Toolbar is a flex row, and flex children shrink by default. With nothing declared, a narrow
 * viewport — half the screen with DevTools open is the ordinary case, not an edge one — squeezed
 * every control at once until "Standard", "Advanced" and the tenant name were wrapping one
 * character per line. So every control is `flexShrink: 0` and `noWrap`, and past the point where
 * they no longer fit the bar scrolls sideways — a control whose label has been folded into a
 * vertical column of letters is not a smaller control, it is an unusable one.
 */
export function AppTopBar({ onToggleNav, navCollapsed = false, navId }: AppTopBarProps): ReactNode {
  const { t, i18n } = useTranslation('shell');
  const { viewLevel, setViewLevel } = useViewLevel();
  const { mode, setMode } = useColorScheme();
  const chrome = useChromeVariant();
  const suite = chrome.variant === 'suite';
  const [helpAnchor, setHelpAnchor] = useState<HTMLElement | null>(null);

  const isDark = mode === 'dark';
  const language = i18n.language.split('-')[0] ?? 'de';
  const changeLanguage = (next: string): void => {
    persistLanguage(next);
    void i18n.changeLanguage(next);
  };

  // The help menu (SDD-022 D-9), in both chromes (suite: after the language): the presentation,
  // the user guide as PDF and the API reference — static files under the console's own origin
  // (T-52). No permission: documentation is for everyone who can sign in. The legacy suite's
  // support page is no longer offered (owner, 2026-10-05: it led to the suite's sign-in).
  const helpEntries = [
    { id: 'presentation', href: '/presentation/', label: t('topbar.helpPresentation') },
    { id: 'guide', href: '/guides/creating-a-group-tarif.pdf', label: t('topbar.helpGuide') },
    { id: 'apidocs', href: '/api-docs/', label: t('topbar.helpApiDocs') },
  ] as const;
  const helpMenu = (
    <>
      <Tooltip title={t('topbar.helpMenu')}>
        <IconButton
          onClick={(event) => {
            setHelpAnchor(event.currentTarget);
          }}
          aria-label={t('topbar.helpMenu')}
          aria-haspopup="menu"
          aria-expanded={helpAnchor !== null}
          aria-controls={helpAnchor !== null ? 'topbar-help-menu' : undefined}
          data-testid="topbar-help"
          sx={FIXED}
        >
          <HelpOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Menu
        id="topbar-help-menu"
        anchorEl={helpAnchor}
        open={helpAnchor !== null}
        onClose={() => {
          setHelpAnchor(null);
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {helpEntries.map((entry) => (
          <MenuItem
            key={entry.id}
            component="a"
            href={entry.href}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`topbar-help-${entry.id}`}
            onClick={() => {
              setHelpAnchor(null);
            }}
          >
            <ListItemText>
              {entry.label}
              {/* Said, not only drawn: the icon beside it is decorative. */}
              <Box component="span" sx={SCREEN_READER_ONLY}>
                {` (${t('topbar.helpNewTab')})`}
              </Box>
            </ListItemText>
            <ListItemIcon sx={{ justifyContent: 'flex-end' }}>
              <OpenInNewIcon fontSize="small" aria-hidden />
            </ListItemIcon>
          </MenuItem>
        ))}
      </Menu>
    </>
  );

  return (
    <AppBar
      position="sticky"
      sx={{
        bgcolor: 'surfaces.toolbar',
        // Neither draws a border: the legacy header floats on its shadow (`header.shadow`), and
        // berry's hairline is the theme's AppBar shadow (`elevation.chrome`).
        borderBottom: 0,
        color: 'text.primary',
      }}
    >
      <Toolbar
        sx={{
          minHeight: chrome.headerHeight,
          gap: 1.5,
          // Never wrap onto a second row: the bar is `position: sticky` and a second row would
          // push the page content down by a line at exactly the widths where space is scarcest.
          flexWrap: 'nowrap',
          // Past the point where even the fixed items no longer fit, the bar scrolls sideways
          // rather than crushing them. Overflow is the honest failure mode; illegible is not.
          overflowX: 'auto',
        }}
        variant="dense"
      >
        {onToggleNav !== undefined && (
          <Tooltip title={navCollapsed ? t('topbar.expandNav') : t('topbar.collapseNav')}>
            <IconButton
              onClick={onToggleNav}
              // The label states the ACTION, and `aria-expanded` states the state; a label that
              // flipped between "open" and "close" would fight the state a screen reader reads.
              aria-label={t('topbar.toggleNav')}
              aria-expanded={!navCollapsed}
              aria-controls={navId}
              data-testid="nav-toggle"
              sx={{
                ...FIXED,
                borderRadius: (theme) => `${String(theme.eis.surface.controlRadius)}px`,
              }}
            >
              <MenuIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        <Box sx={{ flex: 1 }} />

        <ToggleButtonGroup
          size="small"
          exclusive
          value={viewLevel}
          onChange={(_event, next: string | null) => {
            if (next === 'standard' || next === 'advanced') setViewLevel(next);
          }}
          aria-label={t('topbar.viewLevel')}
          sx={{ ...FIXED, '& .MuiToggleButton-root': { whiteSpace: 'nowrap' } }}
        >
          <ToggleButton value="standard">{t('topbar.viewLevel')}: Standard</ToggleButton>
          <ToggleButton value="advanced">Advanced</ToggleButton>
        </ToggleButtonGroup>

        {!suite && helpMenu}

        {suite ? (
          <TextField
            select
            value={language}
            onChange={(event) => {
              changeLanguage(event.target.value);
            }}
            slotProps={{ htmlInput: { 'aria-label': t('topbar.language') } }}
            sx={{ ...FIXED, width: 88 }}
          >
            <MenuItem value="de">DE</MenuItem>
            <MenuItem value="en">EN</MenuItem>
          </TextField>
        ) : (
          // Berry: the design's segmented DE/EN switch instead of a select.
          <ToggleButtonGroup
            exclusive
            value={language}
            onChange={(_event, next: string | null) => {
              if (next !== null) changeLanguage(next);
            }}
            aria-label={t('topbar.language')}
            sx={{ ...FIXED, '& .MuiToggleButton-root': { px: 1.25, minWidth: 40 } }}
          >
            <ToggleButton value="de">DE</ToggleButton>
            <ToggleButton value="en">EN</ToggleButton>
          </ToggleButtonGroup>
        )}

        {suite && helpMenu}

        {!suite && (
          <Tooltip title={t('topbar.colorScheme')}>
            <IconButton
              onClick={() => {
                setMode(isDark ? 'light' : 'dark');
              }}
              aria-label={t('topbar.colorScheme')}
              sx={FIXED}
            >
              {isDark ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        )}

        {/* The caller's exports and imports (SDD-020 §9): a function, so in both chromes. */}
        <JobsTray />

        <Divider orientation="vertical" flexItem sx={{ ...FIXED, my: 1 }} />

        <ProfileChip />
      </Toolbar>
    </AppBar>
  );
}
